import { useState, useMemo } from "react";
import { Breadcrumb, Dropdown, Menu, MenuItem } from "semantic-ui-react";
import { Organization } from "../models/Organization";
import { Notification } from "../models/Notification";
import { LoginMenu } from "./api-authorization/LoginMenu";
import ContactSupportModalContainer from "../containers/ContactSupportModalContainer";
import NotificationsMenu from "./NotificationsMenu";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { PermitRegister } from "../models/PermitRegister";

interface Props {
  orgName?: string;
  projectName?: string;
  registerTitle?: string;
  organizations?: Organization[];
  myRegisters?: PermitRegister[];
  onOrgChange: (name: string) => void;
  notifications?: Notification[];
  onDismissNotifications: (ids: string[]) => void;
  onRegisterChange: (projectName: string, registerTitle: string) => void;
  onHomeClick: () => void;
}

const MainHeader = ({
  notifications,
  onDismissNotifications,
  orgName,
  projectName,
  registerTitle,
  organizations,
  myRegisters,
  onOrgChange,
  onRegisterChange,
  onHomeClick,
}: Props) => {
  const [openContactSupportModal, setOpenContactSupportModal] = useState(false);

  const handleOrgClick = (e: any, name: string): void => {
    onOrgChange(name);
  };
  const { user } = useCurrentUser();

  // Group registers by project
  const projectsWithRegisters = useMemo(() => {
    if (!myRegisters) return [];
    const map = new Map<string, { projectName: string; registers: PermitRegister[] }>();
    for (const reg of myRegisters) {
      if (!reg.project) continue;
      const key = reg.project.id;
      if (!map.has(key)) {
        map.set(key, { projectName: reg.project.name, registers: [] });
      }
      map.get(key)!.registers.push(reg);
    }
    return Array.from(map.values());
  }, [myRegisters]);

  const renderBreadcrumb = () => {
    if (orgName) {
      return (
        <Menu.Item>
          <Breadcrumb>
            <Breadcrumb.Section style={{ padding: "0 10px 0 10px" }}>
              <Dropdown
                trigger={<>{orgName}</>}
                style={{
                  padding: "0px",
                }}
                icon={null}
              >
                <Dropdown.Menu>
                  {organizations &&
                    organizations.map((org) => (
                      <Dropdown.Item key={org.id} onClick={(e) => handleOrgClick(e, org.name)}>
                        {org.name}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            </Breadcrumb.Section>

            {projectName && (
              <>
                <Breadcrumb.Divider />
                <Breadcrumb.Section active style={{ padding: "0 10px 0 10px" }}>
                  <Dropdown
                    trigger={<>{projectName}</>}
                    style={{
                      padding: "0px",
                    }}
                    icon={null}
                  >
                    <Dropdown.Menu>
                      {projectsWithRegisters.map((group) => (
                        <Dropdown.Item>
                          <Dropdown key={group.projectName} text={group.projectName}>
                            <Dropdown.Menu>
                              {group.registers.map((r) => (
                                <Dropdown.Item key={r.id} onClick={() => onRegisterChange(group.projectName, r.title)}>
                                  {r.title}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Breadcrumb.Section>
              </>
            )}

            {registerTitle && (
              <>
                <Breadcrumb.Divider />
                <Breadcrumb.Section active style={{ padding: "0 10px 0 10px" }}>
                  <Dropdown
                    trigger={<>{registerTitle}</>}
                    style={{
                      padding: "0px",
                    }}
                    icon={null}
                  >
                    <Dropdown.Menu>
                      {myRegisters
                        ?.filter((r) => r.project?.name === projectName)
                        .map((r) => (
                          <Dropdown.Item key={r.id} onClick={() => onRegisterChange(projectName!, r.title)}>
                            {r.title}
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Breadcrumb.Section>
              </>
            )}
          </Breadcrumb>
        </Menu.Item>
      );
    } else {
      return "";
    }
  };

  return (
    <>
      <Menu color="green" inverted fixed="top" borderless size="massive" style={{ boxShadow: "none" }}>
        <Menu.Item header onClick={onHomeClick}>
          VISLOCK
        </Menu.Item>
        {renderBreadcrumb()}
        <Menu.Menu position="right">
          <MenuItem
            name="Knowledge Hub"
            onClick={() => {
              window.open("https://kb.vislock.co.uk", "_blank");
            }}
          ></MenuItem>
          <MenuItem
            name="Help desk"
            onClick={() => {
              setOpenContactSupportModal(true);
            }}
          ></MenuItem>
          {user && <NotificationsMenu onDismissNotifications={onDismissNotifications} notifications={notifications} />}
          <LoginMenu />
        </Menu.Menu>
      </Menu>

      <ContactSupportModalContainer
        open={openContactSupportModal}
        onClose={() => {
          setOpenContactSupportModal(false);
        }}
      />
    </>
  );
};

export default MainHeader;
